import React from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { type ILocationDetailsSchema } from '../helper';
import { AT_UNIT_METERS, formatDistance, haversineMeters } from '../location/geo';
import { CardSection, SummaryHeader, SummarySection } from '../styled';

interface ISummaryProps {
	hookForm: UseFormReturn<ILocationDetailsSchema>;
	onEdit: () => void;
	/**
	 * The role chosen on the Field Readiness step. Distance is defined only for
	 * polling-unit agents (EC8A): a ward or LGA collation officer is not going
	 * to the unit, so the row is omitted for them. Unknown → shown.
	 */
	resultType?: string;
}

export const DISTANCE_NOT_AVAILABLE = 'Not available';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * "About 2.3 km (straight line)", "At the polling unit", or "Not available"
 * when either the applicant's fix or the unit's coordinates are missing.
 */
export const describeDistance = (values: Partial<ILocationDetailsSchema>): string => {
	const { latitude, longitude, puLatitude, puLongitude } = values;
	if (!isFiniteNumber(latitude) || !isFiniteNumber(longitude) || !isFiniteNumber(puLatitude) || !isFiniteNumber(puLongitude)) {
		return DISTANCE_NOT_AVAILABLE;
	}
	const meters = haversineMeters({ lat: latitude, lng: longitude }, { lat: puLatitude, lng: puLongitude });
	return meters < AT_UNIT_METERS ? formatDistance(meters) : `${formatDistance(meters)} (straight line)`;
};

const SummaryLocation = ({ hookForm, onEdit, resultType }: ISummaryProps) => {
	const values = hookForm.watch();
	const showDistance = !resultType || resultType.toUpperCase() === 'EC8A';
	// The raw latitude / longitude still travel to the API; the applicant sees
	// what they mean instead of two six-decimal numbers.
	const data = [
		{
			label: 'Polling Unit',
			value: values.entityName,
		},
		{
			label: 'Polling Unit Code',
			value: values.entityCode,
		},
		...(showDistance ? [{ label: 'Distance from you', value: describeDistance(values) }] : []),
	];
	return (
		<div>
			<SummaryHeader>
				<p
					className='header'
					onClick={onEdit}
				>
					Location
				</p>
				<p className='link'>Edit</p>
			</SummaryHeader>
			<SummarySection>
				{data.map((dataItem) => (
					<CardSection key={dataItem.label}>
						<p className='label'>{dataItem.label}</p>
						<h5 className='value'>{dataItem.value}</h5>
					</CardSection>
				))}
			</SummarySection>
		</div>
	);
};

export default SummaryLocation;
